import { defineStore } from "pinia";
import { saveAuth, loadAuth, clearAuth } from "@/helper/localStorage";
import { authStorage } from "@/services/authService";
import {
  clearPermissions,
  getAccessSnapshot,
  getPermissions,
  saveAccessSnapshot,
} from "@/helper/permissionHelper";
import usePosSalesStore from "@/stores/posSales.store";
import type { AccessSnapshot } from "@/type/permissions";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    api_key: null as string | null,
    api_secret: null as string | null,
    user: null as any | null,
    // reactive permissions stored for current user
    permissions: [] as string[],
    accessSnapshot: null as AccessSnapshot | null,
    // OAuth2 token-based auth
    access_token: null as string | null,
    refresh_token: null as string | null,
    session_authenticated: false,
    username: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => {
      return !!state.access_token || !!state.refresh_token || state.session_authenticated || (!!state.api_key && !!state.api_secret);
    },
  },

  actions: {
    setAuth(apiKey: string, apiSecret: string, user: any) {
      this.api_key = apiKey;
      this.api_secret = apiSecret;
      this.user = user;

      saveAuth(apiKey, apiSecret, user);
    },

    // New method for OAuth2 token-based login
    setOAuthTokens(
      accessToken: string,
      refreshToken: string | null,
      username: string,
    ) {
      this.access_token = accessToken;
      this.refresh_token = refreshToken;
      this.session_authenticated = false;
      this.username = username;
      this.user = {
        ...(this.user ?? {}),
        username,
        name: username,
      };

      // Also save to localStorage for persistence
      authStorage.setAccessToken(accessToken);
      authStorage.setRefreshToken(refreshToken);
      authStorage.setSessionAuthenticated(false);
      authStorage.setUsername(username);
      authStorage.setUserProfile(this.user);
    },

    setSessionAuth(username: string, user: any = null) {
      this.access_token = null;
      this.refresh_token = null;
      this.session_authenticated = true;
      this.username = username;
      this.user = {
        ...(this.user ?? {}),
        ...(user ?? {}),
        username,
        name: user?.name || username,
      };

      authStorage.setAccessToken(null);
      authStorage.setRefreshToken(null);
      authStorage.setTokenExpiry(null);
      authStorage.setSessionAuthenticated(true);
      authStorage.setUsername(username);
      authStorage.setUserProfile(this.user);
    },

    loadFromStorage() {
      // Try to load OAuth2 tokens first
      const accessToken = authStorage.getAccessToken();
      const refreshToken = authStorage.getRefreshToken();

      if (accessToken || refreshToken) {
        this.access_token = accessToken;
        this.refresh_token = refreshToken;
        this.username = authStorage.getUsername();
        this.user = authStorage.getUserProfile();
      }

      if (authStorage.getSessionAuthenticated()) {
        this.session_authenticated = true;
        this.username = authStorage.getUsername();
        this.user = authStorage.getUserProfile();
      }

      // Fall back to old API key/secret auth
      const data = loadAuth();
      if (data.api_key && data.api_secret) {
        this.api_key = data.api_key;
        this.api_secret = data.api_secret;
        this.user = data.user;
      }

      this.accessSnapshot = getAccessSnapshot();
      this.permissions = this.accessSnapshot?.roles?.length
        ? this.accessSnapshot.roles
        : getPermissions();
    },

    setPermissions(perms: string[]) {
      this.permissions = perms || [];
    },

    setAccessSnapshot(snapshot: AccessSnapshot | null) {
      const savedSnapshot = saveAccessSnapshot(snapshot);

      this.accessSnapshot = savedSnapshot;
      this.permissions = savedSnapshot?.roles ?? [];
    },

    setUserProfile(user: any) {
      this.user = {
        ...(this.user ?? {}),
        ...(user ?? {}),
      };

      if (user?.name || user?.email || user?.username)
        this.username = user.name || user.email || user.username;

      authStorage.setUsername(this.username);
      authStorage.setUserProfile(this.user);
    },

    logout() {
      // Read before the tokens go: the cache purge needs to know whose entries to drop.
      const signedOutUser = String(this.username ?? authStorage.getUsername() ?? "").trim();

      // THEN clear localStorage after state is cleared
      authStorage.clearTokens();
      clearAuth();
      localStorage.removeItem("alkokh_active_cashier_profile");
      // Open POS sales are this user's; the next one to sign in must not see them.
      usePosSalesStore().reset();
      clearPermissions();

      /**
       * Cached reads belong to whoever fetched them, and on a shared till the next
       * person is a different user. The entries are keyed by username so this drops
       * only theirs, and it is fire-and-forget: a cache that will not clear must
       * never block a sign-out. Unsent work lives in a DIFFERENT database and is
       * untouched by this.
       */
      void import("@/services/frappe/cache")
        .then(({ invalidateUser }) => invalidateUser(signedOutUser))
        .catch(() => {});
      // Clear Pinia store state FIRST to prevent race conditions
      // where localStorage might be re-read during state updates
      this.access_token = null;
      this.refresh_token = null;
      this.session_authenticated = false;
      this.username = null;
      this.api_key = null;
      this.api_secret = null;
      this.user = null;
      this.permissions = [];
      this.accessSnapshot = null;
    },
  },
});
